// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMActionService } from '@services/lm-action.service';
import { AppConstants, LMActionRequestStatus, LMDialogButtonKey, LMFormType } from '@utils/app.constants';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMToolbarServiceBase } from '@toolbar/lm-toolbar-service.base';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMFormActionUtilService } from './lm-form-action-util.service';
import { LMFormSpecUtilService } from './lm-form-spec-util.service';
import { LMContextService } from '@services/lm-context.service';

import * as urltemplate from 'url-template';
import { sortBy } from 'lodash-es';
import { LMToolbarServiceAdapter } from '@toolbar/lm-toolbar-service-adapter';
import { LMFormatUtilService, LMUrlUtilService } from '@services/utils';
import { LMUrlInfoBuilderService } from '@app/services';

@Injectable()
export class LMFormToolbarService extends LMToolbarServiceBase {
  private isCreateMode = false;
  private isSmartPhone = false;

  constructor(
    private lmActionService: LMActionService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmContextService: LMContextService,
    private lmFormActionUtilService: LMFormActionUtilService,
    private lmLoggerService: LMLoggerService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmFormSpecUtilService: LMFormSpecUtilService,
    public lmToolbarServiceAdapter: LMToolbarServiceAdapter,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmUrlUtilService: LMUrlUtilService
  ) {
    super(lmToolbarServiceAdapter);
    // don't push all buttons into overflow on smartphone when looking at mingle inbox
    this.isSmartPhone = this.lmContextService.isSmartPhone() && !this.lmContextService.inMingleInbox();
  }

  public buildToolbarButtons(formUrlInfo: LMFormUrlInfo, formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): void {
    this.updateToolbarState(formUrlInfo, formSpec, formStateInfo);
  }

  public updateToolbarState(formUrlInfo: LMFormUrlInfo, formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): void {
    // -----------------------------------------------------------
    // Must rebuild the toolbar so that actions in the overflow
    // menu will be available for the dataView.
    // TODO ppatton - look into building the static part of the
    // toolbar during a single build and only rebuilding the
    // dynamic actions menu in UpdateToolbar. May not be worth
    // the effort as I can't imagine it will save much processing
    // time.
    // -----------------------------------------------------------
    try {
      this.toolbarButtons = [];
      this.overlayToolbarButtons = [];

      // LMWEB-25732: Set isCreateMode early so it is available for all paths including the overlay early-return.
      if (!formSpec.busFormSpec.isWizard && !formStateInfo.disableAllActions) {
        const actionSpec = (formStateInfo && formStateInfo.dataView) ?
          this.lmFormActionUtilService.getTargetAction(formSpec, formStateInfo.dataView) : undefined;
        this.isCreateMode = !!(actionSpec && actionSpec.type === AppConstants.ACTION_TYPE_CREATE);
      }

      if (this.lmContextService.useListFormOverlay()) {
        // building the overlay toolbar. Next to the title, this should be
        // next button, previous button, open details button and
        // the overlay close button
        if (formStateInfo.inOverlay) {
          const actionReferenceArray = [
            this.getStandardToolbarActions().prevRecord,
            this.getStandardToolbarActions().nextRecord,
            this.getStandardToolbarActions().detailsOverlay,
            this.getStandardToolbarActions().closeOverlay
          ];
          this.overlayToolbarButtons = this.actionRefArrayToToolbarButtonArray(formSpec, actionReferenceArray, false);
          this._updateToolbarState(this.overlayToolbarButtons, formUrlInfo, formSpec, formStateInfo);
          this.fireOverlayToolbarObservable();

          // building the secondary overlay toolbar buttons. These should be save and related (drill points) buttons
          const addedOverlayActionRefs = [ this.getStandardToolbarActions().overlaySave ];
          if (!this.isCreateMode) {
            addedOverlayActionRefs.push(this.getStandardToolbarActions().drillOverlay);
          }
          addedOverlayActionRefs.push(this.getStandardToolbarActions().actions);
          this.toolbarButtons = this.actionRefArrayToToolbarButtonArray(formSpec, addedOverlayActionRefs, this.isSmartPhone);
          this._updateToolbarState(this.toolbarButtons, formUrlInfo, formSpec, formStateInfo);
          this.fireToolbarObservable();
          return;
        }
      }

      if (formSpec.drillAroundMode) {
        this.buildDrillAroundToolbarButtons(formSpec, formStateInfo);
      } else {
        // LMCLIENT-33564: BusTask/ActionForm direct link
        const mingleInbox = (this.lmContextService.inMingleInbox() || formUrlInfo.fromInbox || this.lmContextService.inMingleOverlay());
        if (this.lmFormSpecUtilService.isBusinessTask(formSpec) || (this.lmFormSpecUtilService.isActionForm(formSpec) && !mingleInbox)) {
          formStateInfo.disableAllActions = true;
          this.toolbarButtons = this.getBustaskToolbarButtons(formSpec, formStateInfo.dataView);
        }

        if (!formStateInfo.disableAllActions) {
          if (!formSpec.busFormSpec.isWizard) {
            if (formStateInfo && formStateInfo.dataView && formStateInfo.dataView.processFlowActions) {
              this.toolbarButtons = this.getProcessFlowActionToolbarButtons(formUrlInfo, formSpec, formStateInfo);
            } else if (this.lmContextService.inMingleOverlay()) {
              this.toolbarButtons = this.getMingleOverlayToolbarButtons(formSpec, formStateInfo);
            } else if (formSpec.busFormSpec.toolbar) {
              this.toolbarButtons = this.getCalledOutToolbarButtons(formSpec, formStateInfo);
            } else {
              this.toolbarButtons = this.getStandardToolbarButtons(formSpec, formStateInfo);
            }
          }
        }
      }

      this._updateToolbarState(this.toolbarButtons, formUrlInfo, formSpec, formStateInfo);
      this.fireToolbarObservable();
    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  // ----------------------------------------------------------------------------------------
  // PRIVATE METHODS
  // ----------------------------------------------------------------------------------------
  private buildDrillAroundToolbarButtons(formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): void {
    const calledOutActionRefs: LMBusActionReference[] = [];
    // calledOutActionRefs.push(this.getStandardToolbarActions().drillAround);

    let calledOutToolbarButtons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(formSpec, calledOutActionRefs, this.isSmartPhone);
    const moreMenuItems: LMToolbarButton[] = this.getAllDrillAroundMenuItems(formSpec, formStateInfo, calledOutActionRefs, false, true);
    calledOutToolbarButtons = [ ... calledOutToolbarButtons, ... moreMenuItems ];
    this.toolbarButtons = [ ...this.toolbarButtons, ...calledOutToolbarButtons ];
  }

  private getAllDrillAroundMenuItems(
    formSpec: LMFormSpec,
    formStateInfo: LMFormStateInfo,
    excludedActionRefs?: Array<LMBusActionReference>,
    includeCreateActions?: boolean,
    isContextMenu?: boolean
  ): Array<LMToolbarButton> {
    const validActionRefs: Array<LMBusActionReference> = [];

    if (!this.lmContextService.isMobileDevice()) {
      if (!this.lmContextService.simplifyOptionsMenu()) {
        // OPTIONS MENU
        if (!this.lmContextService.inMingleOverlay()) {
          validActionRefs.push(this.getStandardToolbarActions().options);
        }
      } else {
        if (!this.lmContextService.inMingleOverlay()) {

          if (this.lmContextService.isGenAISummarize() && this.lmContextService.isDeveloper() &&
              (formSpec.busFormSpec.formType === LMFormType.COMPOSITE
                  && !formSpec.busFormSpec.panelNavigation
                  && !formSpec.busFormSpec.isWizard)) {
            validActionRefs.push(this.getStandardOptionsMenuItems().genAISummarize);
          }

          // Export MENU
          const optionsConfigFeatures = this.lmContextService.getOptionsConfigFeatures();
          const isMatrixForm = formSpec.busFormSpec.formType === LMFormType.MATRIX;
          if (!this.isCreateMode) {
            if (!optionsConfigFeatures?.disallowExport
                && optionsConfigFeatures?.noPdfInConfig
                && optionsConfigFeatures?.noCsvInConfig
                && isMatrixForm) {
              validActionRefs.push(this.getStandardToolbarActions().export);
            } else if (!optionsConfigFeatures?.disallowExport
                && optionsConfigFeatures?.noPdfInConfig && !isMatrixForm) {
              validActionRefs.push(this.getStandardToolbarActions().exportToPDF);
            } else if (!optionsConfigFeatures?.noPdfInConfig && optionsConfigFeatures?.noCsvInConfig && isMatrixForm) {
              validActionRefs.push(this.getStandardToolbarActions().exportToCsv);
            }
          }
        }
      }
    }
    const allActionsMenuItems = this.createToolbarMenuItemsFromActionReferences(formSpec, validActionRefs, true);

    const optionsMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_OPTIONSMENU_BUTTON);

    if (!this.lmContextService.simplifyOptionsMenu()) {
      if (optionsMenuButton) {
        this.buildOptionsMenu(optionsMenuButton, formSpec, formStateInfo, true);
      }
    } else {
      // Export
      const exportMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_EXPORTMENU_BUTTON);
      if (exportMenuButton) {
        this.buildExportMenu(exportMenuButton, formSpec);
      }
    }

    if (validActionRefs.length === 0) {
      return [];
    }

    return allActionsMenuItems;
  }

  private _updateToolbarState(toolbarButtons: Array<LMToolbarButton>, formUrlInfo: LMFormUrlInfo, formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): void {
    if (!formStateInfo.dataView) {
      return;
    }

    try {
      formStateInfo.disableSave = formStateInfo.isModified === undefined || !formStateInfo.isModified ? true : false;
      formStateInfo.disableSaveAndNew = formStateInfo.includeSaveAndNew !== undefined ? formStateInfo.disableSave : true;

      toolbarButtons.map(
        (toolbarButton: LMToolbarButton) => {
          // Update state for non menu buttons
          this.setStateByActionType(formUrlInfo, formSpec, toolbarButton, formStateInfo);

          // ----------------------------------------------------
          // if the createMenuButton is in the toolbarButtons
          // then build it's menu item.
          // ----------------------------------------------------
          if (toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON) {
            this.buildCreateMenuButtonMenu(toolbarButton, formSpec, formStateInfo.dataView);
          }

          if (formStateInfo.inOverlay && toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_DRILLOVERLAY) {
            this.buildDrillMenPlaceholder(formSpec, toolbarButton);
          }

          if (formStateInfo.inOverlay && toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_ACTIONSMENU_BUTTON) {
            this.buildFormActionsButtonMenu(formStateInfo, formUrlInfo, toolbarButton, formSpec);
          }
        });

      toolbarButtons.map(toolbarButton => {
        if (toolbarButton.menu) {
          toolbarButton.menu.map((item: LMToolbarButton) =>
            this.setStateByActionType(formUrlInfo, formSpec, item, formStateInfo));
        }
      });
    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  private getBustaskToolbarButtons(formSpec: LMFormSpec, dataView: LMDataView): LMToolbarButton[] {
    let submitLabel = Soho.Locale.translate(LMDialogButtonKey.SUBMIT);
    if (formSpec.busFormSpec.submitLabel) {
      submitLabel = this.lmFormatUtilService.formatMessageRule(formSpec.busFormSpec.submitLabel, dataView);
    }
    const actionRefs = [
      {
        id: AppConstants.STANDARD_SAVE,
        type: 'standard',
        label: submitLabel,
      } as LMBusActionReference
    ];
    const actionSpec = this.lmActionService.getTargetAction({ formSpec, dataView });
    if (actionSpec?.willRunAsynchronous && !this.lmContextService.isDataModificationNotAllowed()) {
      actionRefs.splice(0, 0, {
        id: AppConstants.STANDARD_TOOLBAR_SCHEDULE,
        type: 'standard',
        label: Soho.Locale.translate(LMDialogButtonKey.SCHEDULE)
      });
    }
    return this.actionRefArrayToToolbarButtonArray(formSpec, actionRefs, this.isSmartPhone);
  }

  private getProcessFlowActionToolbarButtons(formUrlInfo: LMFormUrlInfo, formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): Array<LMToolbarButton> {
    if (this.lmContextService.isDataModificationNotAllowed()) {
      return [];
    }
    const pfActionRefs = this.pfActionsToActionRefArray(formStateInfo.dataView.processFlowActions);

    // Add Save as draft button if target action is available
    const actionSpec = this.lmFormActionUtilService.getTargetAction(formSpec, formStateInfo.dataView);
    if (actionSpec) {
      pfActionRefs.push(this.getStandardToolbarActions().saveAsDraft);
    }

    if ((this.lmContextService.inMingleInbox() || formUrlInfo.fromInbox ) && formSpec.busFormSpec.formType === LMFormType.ACTION) {
      // Add compare button for action requests
      if (formStateInfo.dataView.canCompare) {
        pfActionRefs.push(this.getStandardToolbarActions().pfCompare);
      }
    }

    const pfActionToolbarButtons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(formSpec, pfActionRefs, this.isSmartPhone);
    return pfActionToolbarButtons;
  }

  public getMingleOverlayToolbarButtons(formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): Array<LMToolbarButton> {
    let toolbarButtons = this.getBustaskToolbarButtons(formSpec, formStateInfo.dataView);

    // include 'Save as Draft'
    let addSaveAsDraftButton = false;
    const dataView = formStateInfo.dataView;
    const isInitiator = dataView.contextProperties && dataView.contextProperties['context_prd_initiator'] !== undefined
        && dataView.contextProperties['context_prd_initiator'] === 'true';
    const isActionRequest = dataView.contextProperties && dataView.contextProperties['context_prd_action_prd_request'] !== undefined
        && dataView.contextProperties['context_prd_action_prd_request'].length > 0;
    const isApprover = dataView.contextProperties && dataView.contextProperties['context_prd_approver'] !== undefined
        && dataView.contextProperties['context_prd_approver'] === 'true';
    const isFinalApprover = dataView.contextProperties && dataView.contextProperties['context_prd_finalapprover'] !== undefined
        && dataView.contextProperties['context_prd_finalapprover'] === 'true';

    if (isInitiator) {
      if (isActionRequest) {
        const notInProcess = (dataView.contextProperties['context_prd_action_prd_request_prd_status']
            && dataView.contextProperties['context_prd_action_prd_request_prd_status'] === LMActionRequestStatus.NOTINPROCESS);
        if (notInProcess) {
          addSaveAsDraftButton = true;
        }
      } else {
        addSaveAsDraftButton = true;
      }
    } else if (isApprover || isFinalApprover) {
      addSaveAsDraftButton = true;
    }

    if (addSaveAsDraftButton) {
      toolbarButtons = toolbarButtons.concat(this.actionRefArrayToToolbarButtonArray(formSpec, [ this.getStandardToolbarActions().saveAsDraft ], this.isSmartPhone));
    }

    return toolbarButtons;
  }

  private getCalledOutToolbarButtons(formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): Array<LMToolbarButton> {
    // make a deep clone of the toolbar so the relActionUrl does not get over-written
    const calledOutActionRefs = this.getCalledOutToolbarActionRefs(formSpec, formStateInfo);
    let calledOutToolbarButtons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(formSpec, calledOutActionRefs, this.isSmartPhone, undefined, !!formStateInfo.inOverlay);

    if (!this.lmContextService.casualMode()) {
      let actionTypes = [
        AppConstants.ACTION_TYPE_DELETE,
        AppConstants.ACTION_TYPE_FUNCTION,
        AppConstants.ACTION_TYPE_UPDATE ];

      if (formStateInfo.isModified) {
        actionTypes = [ ...actionTypes, AppConstants.STANDARD_SAVE ];
      }

      if (!formStateInfo.calledOutActionsOnly) {
        let excludedRefs = calledOutActionRefs;
        if (formStateInfo.inOverlay) {
          const standardActionRefs: Array<LMBusActionReference> = this.getStandardToolbarActionReferences(formSpec);
          excludedRefs = [ ...excludedRefs, ...standardActionRefs ];
        }
        const moreMenuItems = this.getAllActionsMenuItems(formSpec, formStateInfo, excludedRefs, actionTypes);
        calledOutToolbarButtons = [ ...calledOutToolbarButtons, ...moreMenuItems ];
      } else if (this.isSmartPhone) {
        // mobile view and called out actions only. Put all the called out actions into a menu button, instead of into the overflow menu
        if (calledOutToolbarButtons.length > 0) {
          const actionMenuButton = this.buildLMToolbarButton(formSpec, this.getStandardToolbarActions().actions, true, false, false);
          calledOutToolbarButtons.forEach((button: LMToolbarButton) => button.isMoreMenuItem = false);
          actionMenuButton.menu = calledOutToolbarButtons;
          return [ actionMenuButton ];
        }
      }
    }

    return calledOutToolbarButtons;
  }

  private getCalledOutToolbarActionRefs(formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): LMBusActionReference[] {
    if (this.lmContextService.isSmartPhone() && formSpec.calledOutActionsOnly) {
      if (formSpec.busFormSpec.toolbar.filter((action: LMBusActionReference) => action.id === AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON).length === 1) {
        // if called out action includes the create menu, and we are called out actions in smart phone display type, remove the create menu and
        // just add in the create actions themselves
        const calledOutActions: LMBusActionReference[] = JSON.parse(JSON.stringify(formSpec.busFormSpec.toolbar));
        const index = calledOutActions.findIndex((action: LMBusActionReference) => action.id === AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON);
        calledOutActions.splice(index, 1);

        const actions = formSpec.busClassSpec.actions;
        const createActionRefs: Array<LMBusActionReference> = this.actionSpecsToActionRefs(
          this.lmActionService.getCreateActions(actions, formStateInfo.dataView.actionIndexes));

        createActionRefs.sort((actionRef1: LMBusActionReference, actionRef2: LMBusActionReference) => actionRef1.label.localeCompare(actionRef2.label));
        return [ ...createActionRefs, ...calledOutActions ];
      }
    }
    
    const buttonRefs = JSON.parse(JSON.stringify(formSpec.busFormSpec.toolbar));

    if (formStateInfo.inOverlay) {
      // make sure called out standard actions are excluded when in the flyout
      const standardActions = this.getStandardToolbarActions();
      const standardActionIds = Object.values(standardActions).map((action: LMBusActionReference) => action.id);
      return buttonRefs
        .filter((actionRef: LMBusActionReference) => !standardActionIds.includes(actionRef.id))
        .map(actionRef => this.mapCalledOutActionRef(formSpec, actionRef, formStateInfo));
    }

    return buttonRefs
      .map(actionRef =>
        this.mapCalledOutActionRef(formSpec, actionRef, formStateInfo));
  }

  private mapCalledOutActionRef(formSpec: LMFormSpec, actionRef: LMBusActionReference, formStateInfo: LMFormStateInfo): LMBusActionReference {
    if (!actionRef.label && actionRef.labelMessageRule) {
      actionRef.label = this.lmFormatUtilService.formatMessageRule(actionRef.labelMessageRule, formStateInfo.dataView );
    }

    if (actionRef.id === AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON) {

      const actionSpecArray: Array<LMActionSpec> = this.lmActionService.getCreateActions(
        formSpec.busClassSpec.actions, formStateInfo.dataView.actionIndexes);

      // if only one valid create action, display that instead of the called out create menu
      if (actionSpecArray && actionSpecArray.length === 1) {
        const createActionRefs = this.actionSpecsToActionRefs(actionSpecArray);
        const createRef = createActionRefs[0];

        // If there is no label on called out, show no label
        if (!actionRef.label) {
          createRef.label = undefined;
        }

        // if single create doesn't have a icon, use the create menu icon
        if (!createRef.icon) {
          createRef.icon = actionRef.icon;
        }

        return createRef;
      }
    }

    return actionRef;
  }

  private getStandardToolbarButtons(formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): Array<LMToolbarButton>  {
    const standardActionRefs: Array<LMBusActionReference> = this.getStandardToolbarActionReferences(formSpec, !!formStateInfo.inOverlay);
    let standardToolbarButtons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(formSpec, standardActionRefs, this.isSmartPhone, undefined, !!formStateInfo.inOverlay);
    let excludedActionRefs = standardActionRefs;

    if (formStateInfo.inOverlay) {
      // LMWEB-25839: The flyout handles prev/next/save via its own hard-coded buttons, so they
      // are not in standardActionRefs. But they still need to be excluded from the Actions menu
      // to prevent them from appearing in the flyout actions dropdown.
      excludedActionRefs = [
        ...excludedActionRefs,
        this.getStandardToolbarActions().prevRecord,
        this.getStandardToolbarActions().nextRecord,
        this.getStandardToolbarActions().save,
        this.getStandardToolbarActions().saveAndNew,
      ];
    }

    const deleteActionRefs = this.lmActionService.getValidFormDeleteActions( formSpec.busClassSpec.actions, formStateInfo.dataView);
    if (!this.lmActionService.hasStandardDelete(deleteActionRefs)) {
      excludedActionRefs = excludedActionRefs.filter(action => !(action.id === AppConstants.STANDARD_TOOLBAR_DELETE));
    }

    if (formStateInfo.suppressStandardToolbar) {
      standardToolbarButtons.forEach((button: LMToolbarButton) => button.isMoreMenuItem = true);
    }

    const moreMenuItems: Array<LMToolbarButton> = this.getAllActionsMenuItems(formSpec, formStateInfo, excludedActionRefs);

    if (formStateInfo.inOverlay) {
      // don't put the standard actions in the form actions menu in flyout
      standardToolbarButtons = moreMenuItems;
    } else {
      standardToolbarButtons = [ ...standardToolbarButtons, ...moreMenuItems ];
    }

    return standardToolbarButtons;
  }

  private getStandardToolbarActionReferences(formSpec: LMFormSpec, inOverlay = false): Array<LMBusActionReference> {
    const actionReferenceArray: Array<LMBusActionReference> = [];
    const isMatrixForm = formSpec.busFormSpec.formType === LMFormType.MATRIX;
    if (inOverlay) {
      // Flyout handles previous, next, and save as a separate hard coded list, so they
      // can be excluded here to reduce going thru creating LMToolbarButtons that will
      // just be discarded
      actionReferenceArray.push(this.getStandardToolbarActions().create);
      actionReferenceArray.push(this.getStandardToolbarActions().createMenu);
      actionReferenceArray.push(this.getStandardToolbarActions().delete);
      actionReferenceArray.push(this.getStandardToolbarActions().refresh);
    } else if (isMatrixForm) {
      actionReferenceArray.push(this.getStandardToolbarActions().save);
      actionReferenceArray.push(this.getStandardToolbarActions().matrixShowDetails);
      actionReferenceArray.push(this.getStandardToolbarActions().matrixHideDetails);
      actionReferenceArray.push(this.getStandardToolbarActions().refresh);
    } else {
      actionReferenceArray.push(this.getStandardToolbarActions().prevRecord);
      actionReferenceArray.push(this.getStandardToolbarActions().nextRecord);
      actionReferenceArray.push(this.getStandardToolbarActions().create);
      actionReferenceArray.push(this.getStandardToolbarActions().createMenu);
      actionReferenceArray.push(this.getStandardToolbarActions().save);
      actionReferenceArray.push(this.getStandardToolbarActions().saveAndNew);
      actionReferenceArray.push(this.getStandardToolbarActions().delete);
      actionReferenceArray.push(this.getStandardToolbarActions().refresh);

      // if (this.lmContextService.drillAroundEnabled()) {
      //   actionReferenceArray.push(this.getStandardToolbarActions().drillAround);
      // }
    }

    return actionReferenceArray;
  }

  private getAllActionsMenuItems(formSpec: LMFormSpec, formStateInfo: LMFormStateInfo, excludedActionRefs?: Array<LMBusActionReference>, actionTypes?: Array<string>): Array<LMToolbarButton> {
    if (this.lmContextService.casualMode()) {
      return [];
    }

    // all actions for a more or right click menu
    const validActionRefs: Array<LMBusActionReference> = this.getAllValidActionReferences(formSpec, formStateInfo, excludedActionRefs, actionTypes);

    if (!this.lmContextService.isMobileDevice()) {
      // put separator before options/drill
      if (!formStateInfo.inOverlay) {
        if (validActionRefs && validActionRefs.length > 0 && validActionRefs[validActionRefs.length - 1].id !== this.getStandardSeparatorMenuItem().id) {
          validActionRefs.push(this.getStandardSeparatorMenuItem());
        }
      }

      // DRILL AROUND MENU ITEM
      if (this.lmContextService.drillAroundEnabled() && !formStateInfo.inOverlay) {
        validActionRefs.push(this.getStandardToolbarActions().drillAround);
      }

      if (!this.lmContextService.simplifyOptionsMenu()) {
        // OPTIONS MENU
        if (!this.lmContextService.inMingleOverlay() && !formStateInfo.inOverlay) {
          validActionRefs.push(this.getStandardToolbarActions().options);
        }
      } else {
        if (!this.lmContextService.inMingleOverlay()) {
          if (formStateInfo?.dataView?.taggable) {
            validActionRefs.push(this.getStandardOptionsMenuItems().addTag);
          }

          if (this.lmContextService.isGenAISummarize() && this.lmContextService.isDeveloper() &&
            (formSpec.busFormSpec.formType === LMFormType.COMPOSITE
              && !formSpec.busFormSpec.panelNavigation
              && !formSpec.busFormSpec.isWizard)) {
            validActionRefs.push(this.getStandardOptionsMenuItems().genAISummarize);
          }

          // Export MENU
          const optionsConfigFeatures = this.lmContextService.getOptionsConfigFeatures();
          const isMatrixForm = formSpec.busFormSpec.formType === LMFormType.MATRIX;
          if (!this.isCreateMode) {
            if (!optionsConfigFeatures?.disallowExport
                && optionsConfigFeatures?.noPdfInConfig
                && optionsConfigFeatures?.noCsvInConfig
                && isMatrixForm
                && !formStateInfo.inOverlay) {
              validActionRefs.push(this.getStandardToolbarActions().export);
            } else if (!optionsConfigFeatures?.disallowExport
                && optionsConfigFeatures?.noPdfInConfig
                && !isMatrixForm
                && !formStateInfo.inOverlay) {
              validActionRefs.push(this.getStandardToolbarActions().exportToPDF);
            } else if (!optionsConfigFeatures?.noPdfInConfig 
                && optionsConfigFeatures?.noCsvInConfig
                && isMatrixForm
                && !formStateInfo.inOverlay) {
              validActionRefs.push(this.getStandardToolbarActions().exportToCsv);
            }
          }

          // Audit MENU
          if (!formStateInfo.inOverlay)
            validActionRefs.push(this.getStandardToolbarActions().audit);

          // LMWEB-17052 Create Report from Form
          if (formStateInfo.canCreateReport && !formStateInfo.inOverlay) {
            this.getStandardOptionsMenuItems().createReport.label = Soho.Locale.translate('SelectReportFields');
            validActionRefs.push(this.getStandardOptionsMenuItems().createReport);
          }
        }
      }

      // DRILL/RELATED MENU
      if (!formStateInfo.inOverlay)
        validActionRefs.push(this.getStandardToolbarActions().drillAroundMenu);
    }

    // How could this have ever been true?
    // if (validActionRefs.length === 0) {
    //   return [];
    // }

    // make sure all message rules have been formatted
    if (formStateInfo.dataView) {
      validActionRefs.filter((ref: LMBusActionReference) => !ref.label && ref.labelMessageRule)
        .map((ref: LMBusActionReference) => {
          ref.label = this.lmFormatUtilService.formatMessageRule(ref.labelMessageRule, formStateInfo.dataView);
        });
    }

    // ----------------------------------------------------------
    // turn valid action refs into toolbar buttons.
    // ----------------------------------------------------------
    const allActionsMenuItems = this.createToolbarMenuItemsFromActionReferences(formSpec, validActionRefs, true);

    // --------------------------------------------------------------
    // Don't create an options menu button if in casual mode.
    // --------------------------------------------------------------
    if (!this.lmContextService.casualMode()) {
      const optionsMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_OPTIONSMENU_BUTTON);

      if (!this.lmContextService.simplifyOptionsMenu()) {
        if (optionsMenuButton) {
          this.buildOptionsMenu(optionsMenuButton, formSpec, formStateInfo, true);
        }
      } else {
        // Export
        const exportMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_EXPORTMENU_BUTTON);
        if (exportMenuButton) {
          this.buildExportMenu(exportMenuButton, formSpec);
        }

        // Audit
        const auditMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_AUDITMENU_BUTTON);
        if (auditMenuButton) {
          this.buildAuditMenu(auditMenuButton, formSpec, formStateInfo);
        }
      }

      const drillMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_RELATEDMENU_BUTTON);
      if (drillMenuButton) {
        this.buildDrillMenPlaceholder(formSpec, drillMenuButton);
      }
    }

    return allActionsMenuItems;
  }

  private filterValidActionRefs(
    validActionRefs: LMBusActionReference[],
    excludedActionRefs: LMBusActionReference[] = []
  ) {
    if (!excludedActionRefs.length) {
      return validActionRefs;
    }

    return validActionRefs.filter(actionRef =>
      !excludedActionRefs.find(item => {
        // Need to strip off _<index> before comparing to the actionRef.id.
        // .+(?=_\d+$) - to match everything up to an _<number>
        const match = item.id.match(/.+(?=_\d+$)/);
        const itemId = match ? match.toString() : item.id;
        return itemId === actionRef.id;
      })
    );
  }

  private getAllValidActionReferences(
    formSpec: LMFormSpec,
    formStateInfo: LMFormStateInfo,
    excludedActionRefs?: Array<LMBusActionReference>,
    actionTypes?: Array<string>
  ): Array<LMBusActionReference> {

    if (!formStateInfo.dataView) {
      this.lmLoggerService.warn('DataView is undefined, valid action references not populated');
      return;
    }

    const isMatrixForm = formSpec.busFormSpec.formType === LMFormType.MATRIX;
    const isStandardToolbar = !formSpec.busFormSpec.toolbar;
    const actions = formSpec.busClassSpec.actions;
    let validActionRefs: Array<LMBusActionReference> = [];

    // how does this work? validActionRefs is an empty array at this point. Is Matrix Form not supposed to have any actions on it?
    if (isMatrixForm) {
      return this.filterValidActionRefs(validActionRefs, excludedActionRefs);
    }

    if (this.isCreateMode) {
      // SAVE / SAVE AND NEW
      validActionRefs.push(this.getStandardToolbarActions().save);
      validActionRefs.push(this.getStandardActionMenuItems().saveAndNew);
      return this.filterValidActionRefs(validActionRefs, excludedActionRefs);
    }

    // NEXT & PREVIOUS
    validActionRefs.push(this.getStandardToolbarActions().prevRecord);
    validActionRefs.push(this.getStandardToolbarActions().nextRecord);

    let addSeparator = false;

    const includeCreateActions = (actionTypes) ? actionTypes.indexOf(AppConstants.ACTION_TYPE_CREATE) > -1 : true;
    if (includeCreateActions) {
      if (isStandardToolbar) {
        const createActionButton = this.toolbarButtons.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_CREATE);
        if (createActionButton) {
          // CREATE
          validActionRefs.push(this.getStandardToolbarActions().create);
        }
      } else {
        // CREATE / CREATE MENU
        validActionRefs.push(this.getStandardToolbarActions().create);
        validActionRefs.push(this.getStandardToolbarActions().createMenu);
      }
      addSeparator = true;
    }

    // SAVE / SAVE AND NEW / REFRESH
    validActionRefs.push(this.getStandardToolbarActions().save);
    validActionRefs.push(this.getStandardActionMenuItems().saveAndNew);
    validActionRefs.push(this.getStandardToolbarActions().refresh);

    validActionRefs = this.filterValidActionRefs(validActionRefs, excludedActionRefs);

    if (addSeparator && (validActionRefs && validActionRefs.length > 0 && validActionRefs[validActionRefs.length - 1].id !== this.getStandardSeparatorMenuItem().id)) {
      validActionRefs.push(this.getStandardSeparatorMenuItem());
      addSeparator = false;
    }

    // import actions
    const importActionsRefs = this.actionSpecsToActionRefs(this.lmActionService.getImportActions(actions, formStateInfo.dataView.actionIndexes));
    const sortedImportActionRefs = sortBy(importActionsRefs, 'label');
    validActionRefs = validActionRefs.concat(sortedImportActionRefs);
    if (sortedImportActionRefs && sortedImportActionRefs.length > 0) {
      addSeparator = true;
    }
    validActionRefs = this.filterValidActionRefs(validActionRefs, excludedActionRefs);

    if (addSeparator && (validActionRefs && validActionRefs.length > 0 && validActionRefs[validActionRefs.length - 1].id !== this.getStandardSeparatorMenuItem().id)) {
      validActionRefs.push(this.getStandardSeparatorMenuItem());
      addSeparator = false;
    }

    // update and instance actions
    const updateActionRefs = this.actionSpecsToActionRefs(this.lmActionService.getActions(actions, formStateInfo.dataView.actionIndexes, AppConstants.ACTION_TYPE_UPDATE, false, true, false));
    const instanceActionRefs = this.actionSpecsToActionRefs(this.lmActionService.getValidFormInstanceActions(actions, formStateInfo.dataView));
    const updateAndInstanceActionRef = [].concat(updateActionRefs).concat(instanceActionRefs);
    const sortedUpdateAndInstanceActionRef = sortBy(updateAndInstanceActionRef, 'label');
    validActionRefs = validActionRefs.concat(sortedUpdateAndInstanceActionRef);

    // delete actions
    const deleteActionRefs = this.actionSpecsToActionRefs(this.lmActionService.getValidFormDeleteActions(actions, formStateInfo.dataView));
    // is the standard delete on the toolbar?
    if (excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_DELETE)) {
      // do not display a single delete action in the More menu
      if (deleteActionRefs.length > 1) {
        validActionRefs = validActionRefs.concat(deleteActionRefs);
      }
    } else {
      validActionRefs = validActionRefs.concat(deleteActionRefs);
    }

    // if (this.lmContextService.drillAroundEnabled()) {
    //   validActionRefs.push(this.getStandardOptionsMenuItems().drillAround);
    // }

    return this.filterValidActionRefs(validActionRefs, excludedActionRefs);
  }

  private buildCreateMenuButtonMenu(createMenuButton: LMToolbarButton, formSpec: LMFormSpec, dataView: LMDataView): void {
    const actions = formSpec.busClassSpec.actions;
    const createActions = this.actionSpecsToActionRefs(this.lmActionService.getCreateActions(actions, dataView.actionIndexes));
    createMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(formSpec, createActions);
  }

  private buildFormActionsButtonMenu(formStateInfo: LMFormStateInfo, formUrlInfo: LMFormUrlInfo, actionsMenuButton: LMToolbarButton, formSpec: LMFormSpec): void {
    if (formSpec.busFormSpec.toolbar) {
      actionsMenuButton.menu = this.getCalledOutToolbarButtons(formSpec, formStateInfo);
    } else {
      actionsMenuButton.menu = this.getStandardToolbarButtons(formSpec, formStateInfo);
    }

    actionsMenuButton.menu.forEach((button: LMToolbarButton) => {
      button.isMoreMenuItem = false;
      button.isIconOnly = false;
    });

    // GenAI Generated code - Start
    // LMWEB-25694: When a record only has standard actions in the flyout, the Actions
    // dropdown can end up with no visible (non-separator) items, which renders an empty
    // menu and a blank tooltip. Add a "No Actions Available" placeholder so the dropdown
    // always shows a meaningful message. Mirrors the noop pattern used elsewhere
    // (e.g. NoDrillsAvailable / NoActionsAvailable in lm-toolbar-service.base.ts).
    const hasVisibleAction = (actionsMenuButton.menu as Array<LMToolbarButton>).some(
      (button: LMToolbarButton) =>
        button.state?.visible !== false && button.actionReference?.type !== AppConstants.CSS_MENU_SEPARATOR
    );
    if (!hasVisibleAction) {
      actionsMenuButton.menu = [
        {
          actionReference: { id: 'noop', type: 'noop', label: Soho.Locale.translate('NoActionsAvailable') },
          state: { disabled: false, visible: true }
        } as LMToolbarButton
      ];
    }
    // GenAI Generated code - End
  }

  private buildOptionsMenu(
    optionsMenuButton: LMToolbarButton,
    formSpec: LMFormSpec,
    formStateInfo: LMFormStateInfo,
    moreMenuActions: boolean
  ): void {
    const context = this.lmContextService.getContext();
    const disallowExport = context.disallowExport;
    const configuredFeatures = context.configuredFeatures;
    const noPdfInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_ExportToPDF) !== -1;
    const noCsvInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_ExportToCSV) !== -1;
    const isMatrixForm = formSpec.busFormSpec.formType === LMFormType.MATRIX;
    const actions = [];

    if (formStateInfo?.dataView?.taggable) {
      actions.push(this.getStandardOptionsMenuItems().addTag);
    }

    if (this.lmContextService.isGenAISummarize() && this.lmContextService.isDeveloper() &&
      (formSpec.busFormSpec.formType === LMFormType.COMPOSITE
      && !formSpec.busFormSpec.panelNavigation
      && !formSpec.busFormSpec.isWizard)) {
      actions.push(this.getStandardOptionsMenuItems().genAISummarize);
    }

    // Do not allow exporting when in CreateMode LMCLIENT-37786
    if (!disallowExport && noPdfInConfig && !this.isCreateMode) {

      actions.push(this.getStandardOptionsMenuItems().printToFile);
    }

    if (noCsvInConfig && isMatrixForm && !this.isCreateMode) {
      actions.push(this.getStandardOptionsMenuItems().exportToCSV);
    }

    if (!formSpec.drillAroundMode) {
      if (!this.isCreateMode
        && formSpec.busClassSpec
        && (formSpec.busClassSpec.implAuditing || formSpec.busClassSpec.implEffectiveDated)
        && formStateInfo.dataView) {
        if (!formStateInfo.dataView.viewAuditSecured) {
          const auditLogRef: LMBusActionReference = this.getStandardOptionsMenuItems().auditLog;
          auditLogRef.label = this.lmFormatUtilService.formatMessage(auditLogRef.label, formSpec.busClassSpec.name);
          actions.push(auditLogRef);
        }

        if (!formStateInfo.dataView.viewFullAuditSecured) {
          const fullAuditLogRef: LMBusActionReference = this.getStandardOptionsMenuItems().fullAuditLog;
          fullAuditLogRef.label = this.lmFormatUtilService.formatMessage(fullAuditLogRef.label, formSpec.busClassSpec.name);
          actions.push(this.getStandardOptionsMenuItems().fullAuditLog);
        }
      }

      // LMWEB-17052 Create Report from Form
      if (formStateInfo.canCreateReport) {
        this.getStandardOptionsMenuItems().createReport.label = Soho.Locale.translate('SelectReportFields');
        actions.push(this.getStandardOptionsMenuItems().createReport);
      }
    }

    if (optionsMenuButton) {
      optionsMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(formSpec, actions);
    }
  }

  private buildExportMenu(exportMenuButton: LMToolbarButton, formSpec: LMFormSpec): void {
    const exportMenuActionRefs = this.getExportMenuActionRefs(formSpec);
    exportMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(formSpec, exportMenuActionRefs);
  }

  private getExportMenuActionRefs(formSpec: LMFormSpec): Array<LMBusActionReference> {

    try {
      const context = this.lmContextService.getContext();
      const disallowExport = context.disallowExport;
      const configuredFeatures = context.configuredFeatures;
      const noPdfInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_ExportToPDF) !== -1;
      const noCsvInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_ExportToCSV) !== -1;
      const isMatrixForm = formSpec.busFormSpec.formType === LMFormType.MATRIX;
      const actions = [];

      // Do not allow exporting when in CreateMode LMCLIENT-37786
      if (!disallowExport && noPdfInConfig && !this.isCreateMode) {
        this.getStandardOptionsMenuItems().printToFile.label = Soho.Locale.translate('Pdf');
        actions.push(this.getStandardOptionsMenuItems().printToFile);
      }

      if (noCsvInConfig && isMatrixForm && !this.isCreateMode) {
        this.getStandardOptionsMenuItems().exportToCSV.label = Soho.Locale.translate('Csv');
        actions.push(this.getStandardOptionsMenuItems().exportToCSV);
      }
      return actions;

    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  private buildAuditMenu(auditMenuButton: LMToolbarButton, formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): void {
    const exportMenuActionRefs = this.getAuditActionRefs(formSpec, formStateInfo);
    auditMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(formSpec, exportMenuActionRefs);
  }

  private getAuditActionRefs(formSpec: LMFormSpec, formStateInfo: LMFormStateInfo): Array<LMBusActionReference> {

    try {
      const actions = [];

      if (!formSpec.drillAroundMode) {
        if (!this.isCreateMode
            && formSpec.busClassSpec
            && (formSpec.busClassSpec.implAuditing || formSpec.busClassSpec.implEffectiveDated)
            && formStateInfo.dataView) {
          if (!formStateInfo.dataView.viewAuditSecured) {
            const auditLogRef: LMBusActionReference = this.getStandardOptionsMenuItems().auditLog;
            auditLogRef.label = this.lmFormatUtilService.formatMessage(auditLogRef.label, formSpec.busClassSpec.name);
            actions.push(auditLogRef);
          }

          if (!formStateInfo.dataView.viewFullAuditSecured) {
            const fullAuditLogRef: LMBusActionReference = this.getStandardOptionsMenuItems().fullAuditLog;
            fullAuditLogRef.label = this.lmFormatUtilService.formatMessage(fullAuditLogRef.label, formSpec.busClassSpec.name);
            actions.push(this.getStandardOptionsMenuItems().fullAuditLog);
          }
        }
      }
      return actions;

    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  private setStateByActionType(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    toolbarButton: LMToolbarButton,
    formStateInfo: LMFormStateInfo
  ) {

    let type: string;
    if (toolbarButton.actionReference) {
      type = toolbarButton.actionReference.type;
    } else if (toolbarButton.actionSpec) {
      type = toolbarButton.actionSpec.type;

      if (AppConstants.STANDARD_TOOLBAR_TYPES.includes(type)) {
        type = AppConstants.BUTTON_TYPE_STANDARD_ACTION;
      }

    }

    switch (type) {
      case AppConstants.BUTTON_TYPE_STANDARD_ACTION:
        toolbarButton = this.standardActionState(formSpec, toolbarButton, formStateInfo);
        break;

      case AppConstants.BUTTON_TYPE_FORMINVOKE:
        toolbarButton.state = this.getFormInvokeState(formStateInfo, toolbarButton);
        break;

      case AppConstants.BUTTON_TYPE_HTTPURL:
        this.httpUrlActionState(formSpec, toolbarButton, formStateInfo.dataView);
        break;

      case AppConstants.BUTTON_TYPE_NAVIGATION:
        toolbarButton = this.navigationActionState(formSpec, toolbarButton, formStateInfo);
        break;

      case AppConstants.BUTTON_TYPE_ACTION:
        toolbarButton = this.busClassActionState(formSpec, toolbarButton, formStateInfo.dataView);
        break;

      case AppConstants.BUTTON_TYPE_PROCESSFLOW:
        toolbarButton = this.processFlowActionState(formSpec, toolbarButton, formStateInfo);
        break;

      case AppConstants.BUTTON_TYPE_RELATED:
        toolbarButton = this.relatedActionState(formUrlInfo, formSpec, toolbarButton, formStateInfo);
        break;

      case AppConstants.BUTTON_TYPE_BUSTASK:
        this.busTaskActionState(formStateInfo, toolbarButton);
        break;
    }
  }

  private standardActionState(
    formSpec: LMFormSpec,
    toolbarButton: LMToolbarButton,
    formStateInfo: LMFormStateInfo
  ): LMToolbarButton {

    let action: string;

    if (toolbarButton.actionReference) {
      action = toolbarButton.actionReference.id;
    } else if (toolbarButton.actionSpec) {
      action = toolbarButton.actionSpec.type;
    }

    let visible = false;
    let valid = false;

    const isMatrix = formSpec.busFormSpec.formType === LMFormType.MATRIX;
    const includeFormPaging = this.includeFormPaging(formStateInfo.dataView) && !isMatrix;

    const showRelated = (this.lmContextService.getContext().disallowNavigations === undefined || !this.lmContextService.getContext().disallowNavigations);

    switch (action) {
      case AppConstants.STANDARD_TOOLBAR_MATRIXSHOWDETAIL:
      case AppConstants.STANDARD_TOOLBAR_MATRIXHIDEDETAIL:
        if (isMatrix) {
          const matrixFormSpec: LMBusMatrixFormSpec = <LMBusMatrixFormSpec> formSpec.busFormSpec;
          for (let i = 0, l = matrixFormSpec.cells.length; i < l; i++) {
            const displayField = matrixFormSpec.cells[i];
            if (displayField.detail) {
              valid = AppConstants.STANDARD_TOOLBAR_MATRIXSHOWDETAIL === action ? !formStateInfo.showMatrixDetail : formStateInfo.showMatrixDetail;
              break;
            }
          }

          visible = valid;
        }
        break;
      case AppConstants.STANDARD_TOOLBAR_CREATE:
      case AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON:
        if (!isMatrix && !this.isCreateMode) {
          const createActions = this.getValidCreateActions(formSpec, formStateInfo);
          if (action === AppConstants.STANDARD_TOOLBAR_CREATE) {
            valid = createActions.length === 1;
          } else {
            valid = createActions.length > 1;
          }
          visible = valid;
        }
        break;
      case AppConstants.STANDARD_TOOLBAR_SCHEDULE:
      case AppConstants.STANDARD_TOOLBAR_UPDATE:
      case AppConstants.STANDARD_TOOLBAR_OPEN:
        visible = true;
        valid = true;
        break;
      case AppConstants.STANDARD_SAVE:
      case AppConstants.STANDARD_TOOLBAR_SAVECLOSE:
      case AppConstants.STANDARD_TOOLBAR_SAVEASDRAFT:
      case AppConstants.STANDARD_TOOLBAR_SAVEOVERLAY:
        if (this.lmContextService.isDataModificationNotAllowed()) {
          visible = false;
          valid = false;
        } else if (this.isCreateMode) {
          if (formSpec.busFormSpec.showPanelInfo && formSpec.busFormSpec.showPanelInfo.linearWhen) {
            valid = this.lmConditionUtilService.isValid( [ formSpec.busFormSpec.showPanelInfo.linearWhen ], formStateInfo.dataView);
          } else {
            visible = true;
            valid = true;
          }
        } else if (formSpec.busFormSpec.showPanelInfo && formSpec.busFormSpec.showPanelInfo.linearWhen) {
          valid = this.lmConditionUtilService.isValid( [ formSpec.busFormSpec.showPanelInfo.linearWhen ], formStateInfo.dataView);
        } else {
          // Save needs to available in composite forms even if
          // the form is not updateable because one of its dependent list
          // could be updateable.
          // Save needs to be enabled on a Ming.le overlay form even when no changes have been made.
          visible = true;
          valid = this.lmContextService.inMingleOverlay() ? true : formStateInfo.isModified;
        }
        break;
      case AppConstants.STANDARD_TOOLBAR_SAVENEW:
        if (this.lmContextService.isDataModificationNotAllowed()) {
          visible = false;
          valid = false;
        } else if (this.isCreateMode) {
          if (formSpec.busFormSpec.showPanelInfo && formSpec.busFormSpec.showPanelInfo.linearWhen) {
            valid = this.lmConditionUtilService.isValid( [ formSpec.busFormSpec.showPanelInfo.linearWhen ], formStateInfo.dataView);
          } else {
            visible = true;
            valid = true;
          }
        } else {
          // STANDARD_TOOLBAR_SAVENEW is available if there is a create action without parameters or is not an action form
          if (!isMatrix) {
            if (this.getValidCreateActions(formSpec, formStateInfo).filter( (item) => {
              if (!item.hasActionForm && !item.hasParameters) {
                return true;
              }
            }).length > 0) {
              if (formSpec.busFormSpec.showPanelInfo && formSpec.busFormSpec.showPanelInfo.linearWhen) {
                valid = this.lmConditionUtilService.isValid( [ formSpec.busFormSpec.showPanelInfo.linearWhen ], formStateInfo.dataView);
              } else {
                visible = formStateInfo.isModified;
                valid = formStateInfo.isModified;
              }
            }
          }
        }
        break;
      case AppConstants.STANDARD_TOOLBAR_NEXTRECORD:
        if (includeFormPaging) {
          visible = true;
          valid =  formStateInfo.dataView.pagingInfo.hasNext && !formStateInfo.dataView.pagingInfo.nextDisabled;
        }
        break;
      case AppConstants.STANDARD_TOOLBAR_PREVRECORD:
        if (includeFormPaging) {
          visible = true;
          valid = formStateInfo.dataView.pagingInfo.hasPrevious && !formStateInfo.dataView.pagingInfo.previousDisabled;
        }
        break;
      case AppConstants.STANDARD_TOOLBAR_REFRESH:
        visible = true;
        valid = true;
        break;
      case AppConstants.STANDARD_TOOLBAR_DELETE:
        if (!isMatrix) {
          const validDeleteActionSpecs = this.lmActionService.getValidFormDeleteActions(
            formSpec.busClassSpec.actions, formStateInfo.dataView);
          valid = (!this.isCreateMode && this.lmActionService.hasStandardDelete(validDeleteActionSpecs));
          visible = true;
        }
        break;
      case AppConstants.STANDARD_TOOLBAR_ACTIONSMENU_BUTTON:
      case AppConstants.STANDARD_TOOLBAR_ALLACTIONSMENU_BUTTON:
      case AppConstants.STANDARD_TOOLBAR_OPTIONSMENU_BUTTON:
      case AppConstants.STANDARD_TOOLBAR_AUDITMENU_BUTTON:
      case AppConstants.STANDARD_TOOLBAR_EXPORTMENU_BUTTON:
        valid = true;
        visible = true;
        break;
      case AppConstants.STANDARD_TOOLBAR_DRILLAROUND_BUTTON:
      case AppConstants.STANDARD_TOOLBAR_RELATEDMENU_BUTTON:
        if (showRelated) {
          valid = true;
          visible = true;
        }
        break;
      case AppConstants.STANDARD_TOOLBAR_PFCOMPARE:
        valid = true;
        visible = true;
        break;
      case AppConstants.STANDARD_TOOLBAR_EXPORTPDF:
      case AppConstants.STANDARD_TOOLBAR_PRINTTOFILE:
        valid = !this.lmContextService.getContext().disallowExport;
        visible = !this.lmContextService.getContext().disallowExport;
        break;
      case AppConstants.STANDARD_TOOLBAR_ADDTAG:
        valid = true;
        visible = true;
        break;
      case AppConstants.STANDARD_TOOLBAR_GENAISUMMARIZE:
        valid = true;
        visible = true;
        break;
      case AppConstants.STANDARD_TOOLBAR_EXPORTCSV:
        valid = true;
        visible = true;
        break;
      case AppConstants.STANDARD_TOOLBAR_CREATEREPORT:
        valid = true;
        visible = true;
        break;
      case AppConstants.STANDARD_TOOLBAR_CLOSEOVERLAY:
      case AppConstants.STANDARD_TOOLBAR_DETAILSOVERLAY:
        valid = true;
        visible = true;
        break;
      case AppConstants.STANDARD_TOOLBAR_VIEWAUDIT:
      case AppConstants.STANDARD_TOOLBAR_VIEWFULLAUDIT:
        valid = true;
        visible = true;
        break;
    }

    if (valid && toolbarButton.actionReference && toolbarButton.actionReference.validity) {
      valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, formStateInfo.dataView);
    }

    toolbarButton.state.disabled = !valid;
    if (toolbarButton.actionReference && visible) {
      toolbarButton.state.visible = !(toolbarButton.actionReference.hideWhenInvalid && !valid);
    } else {
      toolbarButton.state.visible = visible;
    }
    return toolbarButton;
  }

  private getFormInvokeState(formStateInfo: LMFormStateInfo, toolbarButton: LMToolbarButton): LMToolbarButtonState {
    const dataView = formStateInfo.dataView;
    const valid = !this.lmContextService.isDataModificationNotAllowed() && ( toolbarButton.actionReference.validity
      ? this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView)
      : true );

    return {
      disabled: !valid,
      visible: valid || !toolbarButton.actionReference.hideWhenInvalid
    };
  }

  private httpUrlActionState(
    formSpec: LMFormSpec,
    toolbarButton: LMToolbarButton,
    dataView: LMDataView,
  ): LMToolbarButton {

    const valid = toolbarButton.actionReference.validity
      ? this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView)
      : true;

    // disabled if not valid
    toolbarButton.state.disabled = !valid;
    // hide if (invalid and hideWhenInvalid === true)
    toolbarButton.state.visible = !(!valid && toolbarButton.actionReference.hideWhenInvalid);

    return toolbarButton;
  }

  private navigationActionState(
    formSpec: LMFormSpec,
    toolbarButton: LMToolbarButton,
    formStateInfo: LMFormStateInfo,
  ): LMToolbarButton {
    const dataView = formStateInfo.dataView;
    let valid = formStateInfo.isCreateMode ? false : true;

    if (valid) {
      // check navigation validity in the dataView
      valid = dataView.navigations.indexOf(toolbarButton.actionReference.navigation) > -1;
    }

    // check for navigation button validity
    if (valid && toolbarButton.actionReference.validity) {
      valid = this.lmConditionUtilService.isValid( toolbarButton.actionReference.validity, dataView);
    }

    // disabled if not valid
    toolbarButton.state.disabled = !valid;

    // hide if (invalid and hideWhenInvalid === true)
    toolbarButton.state.visible = !(!valid && toolbarButton.actionReference.hideWhenInvalid);

    return toolbarButton;
  }

  private processFlowActionState(
    formSpec: LMFormSpec,
    toolbarButton: LMToolbarButton,
    formStateInfo: LMFormStateInfo,
  ): LMToolbarButton {
    // Validity / Visibility for process flow button.. If there is one in dataview, it is valid and visible.
    toolbarButton.state.disabled = false;
    toolbarButton.state.visible = true;

    return toolbarButton;
  }

  private busClassActionState(
    formSpec: LMFormSpec,
    toolbarButton: LMToolbarButton,
    dataView: LMDataView
  ): LMToolbarButton {
    const actionSpec: LMActionSpec = this.getActions(formSpec)[ toolbarButton.actionReference.index ];

    if (!actionSpec) {
      // actionSpec is restricted if not in the busClassSpec.actions list.
      toolbarButton.state.disabled = true;

      // todo should restricted actions be made !visible by default?
      toolbarButton.state.visible = false; // !toolbarButton.actionReference.hideWhenInvalid;

      return toolbarButton;
    }

    switch (actionSpec.type) {
      case AppConstants.ACTION_TYPE_CREATE:
      case AppConstants.ACTION_TYPE_CREATE_WITHIN:
      case AppConstants.ACTION_TYPE_SET:
      case AppConstants.ACTION_TYPE_FUNCTION:
      case AppConstants.ACTION_TYPE_DELETE:
      case AppConstants.ACTION_TYPE_UPDATE:
      case AppConstants.ACTION_TYPE_IMPORT:
        // Set and import actions are generally available on a form in create mode.
        let valid = this.isCreateMode ? actionSpec.type === AppConstants.ACTION_TYPE_SET || actionSpec.type === AppConstants.ACTION_TYPE_IMPORT : true;
        // In update mode, other update request actions are generally available.
        if (actionSpec.type === AppConstants.ACTION_TYPE_UPDATE) {
          valid = !this.isCreateMode && actionSpec.requestAction;
        }
        // Check only the noted types above against the dataview for validity.
        if (valid) {
          valid = this.lmActionService.isValidAction(actionSpec, this.getActions(formSpec), dataView.actionIndexes, undefined, false);
        }

        // check for action button validity
        if (valid && toolbarButton.actionReference.validity) {
          valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView);
        }

        // disabled if not valid
        toolbarButton.state.disabled = !valid;

        // hide if (invalid and hideWhenInvalid === true)
        toolbarButton.state.visible = !(!valid && toolbarButton.actionReference.hideWhenInvalid);

        if (actionSpec.type === AppConstants.ACTION_TYPE_UPDATE && !actionSpec.requestAction) {
          // if update action and not a request action, hide the button
          toolbarButton.state.visible = false;
        }

        break;
    }
    return toolbarButton;
  }

  private relatedActionState(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    toolbarButton: LMToolbarButton,
    formStateInfo: LMFormStateInfo,
  ): LMToolbarButton {
    let valid = true;

    if (this.lmContextService.isDataModificationNotAllowed()) {
      toolbarButton.state.disabled = true;
      toolbarButton.state.visible = !toolbarButton.actionReference.hideWhenInvalid;
      return;
    } 
    
    if (toolbarButton.actionReference.validity) {
      valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, formStateInfo.dataView);
    }

    // disabled if not valid
    toolbarButton.state.disabled = !valid;

    // hide if (invalid and hideWhenInvalid === true)
    toolbarButton.state.visible = !(!valid && toolbarButton.actionReference.hideWhenInvalid);

    // ---------------------------------------------------------------------------------
    // A called-out related action that targets a different business class
    // (`action is <BusinessClass>.<ActionName>`) is emitted with a populated relBusClass
    // and an EMPTY relation. Such an action has no related-records menu to resolve, so
    // buildRelatedMenuActionUrl returns null for it; the urltemplate.parse() call further
    // below would then throw on that null url. Return early here to skip the menu-url
    // build. The visible/disabled state was already set above.
    //
    //  See LMWEB-25175.
    // ---------------------------------------------------------------------------------
    if (!toolbarButton.actionReference.relation && toolbarButton.actionReference.relBusClass) {
      toolbarButton.data = JSON.stringify(toolbarButton.actionReference);
      return toolbarButton;
    }

    // todo ppatton strange that this is built here. Seems like this should happen in lm-form itself.
    //  Look at lm-list-view.component.onCardButtonItemMouseEnter to see it's implementation.
    //  One problem here is that it goes through this code twice for the same button.
    if (!formStateInfo.inOverlay) {
      if (toolbarButton.actionReference.id !== AppConstants.STANDARD_TOOLBAR_DRILLOVERLAY) {
        let relActionUrl = this.lmUrlUtilService.buildRelatedMenuActionUrl(formUrlInfo, toolbarButton.actionReference);
        if (!this.lmContextService.useNewSpecUrls()) {
          this.lmUrlInfoBuilderService.compareSpecUrls(
            formUrlInfo,
            toolbarButton.actionReference.name + '.relActionUrl',
            toolbarButton.actionReference.relActionUrl,
            relActionUrl);

          relActionUrl = toolbarButton.actionReference.relActionUrl;
        }

        const relActionTemplate = urltemplate.parse(relActionUrl);
        const resolvedRelActionUrl = relActionTemplate.expand({ resourceId: formStateInfo.dataView.resourceId });
        toolbarButton.actionReference.resolvedRelActionUrl = resolvedRelActionUrl;
      }
    }

    if (formStateInfo.inOverlay && toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_DRILLOVERLAY) {
      toolbarButton.actionReference.drillResourceId = formStateInfo.dataView.resourceId;
    }

    toolbarButton.data = JSON.stringify(toolbarButton.actionReference);

    return toolbarButton;
  }

  private busTaskActionState(formStateInfo: LMFormStateInfo, toolbarButton: LMToolbarButton):LMToolbarButton {
    let valid = true;
    if (this.lmContextService.isDataModificationNotAllowed()) {
      valid = false;
    } else if (toolbarButton.actionReference.validity) {
      valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, formStateInfo.dataView);
    }

    // disabled if not valid
    toolbarButton.state.disabled = !valid;

    // hide if (invalid and hideWhenInvalid === true)
    toolbarButton.state.visible = !(!valid && toolbarButton.actionReference.hideWhenInvalid);

    return toolbarButton;
  }

  private getValidCreateActions(formSpec: LMFormSpec, formStateInfo: LMFormStateInfo) {
    const actions: Array<LMActionSpec> = this.getActions(formSpec);
    return this.lmActionService.getCreateActions(actions, formStateInfo.dataView.actionIndexes);
  }

  private includeFormPaging( dataView: LMDataView) {
    // TODO: check if paging options is available. May be check if urls are available
    return dataView
      && dataView.hasOwnProperty('pagingInfo')
      && dataView.hasOwnProperty('includeFormPaging')
      && dataView.includeFormPaging;
  }

  // override to decide whether an actionRef should be included when building
  // the toolbar buttons.
  protected includeActionRef(spec: any, actionReference: LMBusActionReference) {
    const formSpec: LMFormSpec = <LMFormSpec> spec;
    return true;
  }

  private getActions(formSpec: LMFormSpec): Array<LMActionSpec> {
    return formSpec.busClassSpec.actions;
  }

  private getDisallowRelated(formSpec: LMFormSpec): boolean {
    return this.lmContextService.getContext().disallowNavigations;
  }

  private buildDrillMenPlaceholder(formSpec: LMFormSpec, drillMenuButton: LMToolbarButton) {
    const validActionRefs: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: Soho.Locale.translate('NoDrillsAvailable') } ];
    drillMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(formSpec, validActionRefs);
  }
}
